import { Injectable } from '@angular/core';

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut, signInWithPopup, GoogleAuthProvider, OAuthProvider, AuthProvider } from 'firebase/auth';
import { getFirestore, Firestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, Functions } from 'firebase/functions';
import { getStorage, FirebaseStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {

  private app: FirebaseApp;
  public auth: Auth;
  public firestore: Firestore;
  public functions: Functions;
  public storage: FirebaseStorage;

  constructor() {
    this.app = initializeApp(environment.firebaseConfig);
    this.auth = getAuth(this.app);
    this.firestore = getFirestore(this.app);
    this.functions = getFunctions(this.app, environment.firebaseConfig.region);
    this.storage = getStorage(this.app);
  }

  async login(email: string, password: string) {
    const credential = await signInWithEmailAndPassword(this.auth, email, password);
    return credential.user;
  }

  async register(email: string, password: string) {
    const credential = await createUserWithEmailAndPassword(this.auth, email, password);
    return credential.user;
  }

  async forgotPassword(email: string) {
    await sendPasswordResetEmail(this.auth, email);
  }

  async logout() {
    await signOut(this.auth);
  }

  async getCurrentUser() {
    await this.auth.authStateReady();
    return this.auth.currentUser;
  }

  async signInWithSocial(provider: string) {
    const authProvider = this.getAuthProvider(provider);
    if (authProvider) {
      const userCred = await signInWithPopup(this.auth, authProvider);
      const user = await getDoc(doc(this.firestore, 'users', userCred.user.uid));
      if (!user.exists()) {
        await setDoc(doc(this.firestore, 'users', userCred.user.uid), {
          userId: userCred.user.uid,
          organizationIds: [],
          licenseAccepted: false,
        });
      }
      return userCred.user;
    }
    throw new Error('Invalid provider');
  }

  private getGoogleAuthProvider(): GoogleAuthProvider {
    return new GoogleAuthProvider();
  }

  private getMicrosoftAuthProvider(): OAuthProvider {
    return new OAuthProvider('microsoft.com');
  }

  private getAuthProvider(provider: string): AuthProvider | null {
    switch (provider) {
      case 'google':
        return this.getGoogleAuthProvider();
      case 'microsoft':
        return this.getMicrosoftAuthProvider();
      default:
        return null;
    }
  }

  getAuth() {
    return this.auth;
  }

  getFirestore() {
    return this.firestore;
  }

  getFunctions() {
    return this.functions;
  }

  getStorage() {
    return this.storage;
  }

  /**
   * Upload a file to Firebase Storage
   * @param file The file to upload (File object or base64 string)
   * @param path The storage path where to upload the file
   * @returns Promise<string> The download URL of the uploaded file
   */
  async uploadFile(file: File | string, path: string): Promise<string> {
    const storageRef = ref(this.storage, path);
    
    let uploadData: File | Uint8Array;
    
    if (typeof file === 'string') {
      // Handle base64 string (for AI-generated images)
      const base64Data = file.includes(',') ? file.split(',')[1] : file;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      uploadData = new Uint8Array(byteNumbers);
    } else {
      // Handle File object
      uploadData = file;
    }

    const snapshot = await uploadBytes(storageRef, uploadData);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  }

  /**
   * Delete a file from Firebase Storage
   * @param path The storage path of the file to delete
   */
  async deleteFile(path: string): Promise<void> {
    const storageRef = ref(this.storage, path);
    await deleteObject(storageRef);
  }
}
